// public/js/utils/units.js
// Per-user unit system (metric ⇄ imperial).
//
// The preference is stored in localStorage (instant, offline-friendly). The
// topbar toggle and the Settings → User tab both read/write through here, and
// every display spot that shows a physical quantity converts via convertValue()
// / formatMeasure() so flipping the switch re-labels the whole app.
//
// Conversion is keyed by the *metric* unit string already used across the code
// (°C, km/h, m, …). Units without an entry (%, V, dBm, °, hPa-as-is) are shown
// unchanged in both systems.

const KEY = 'superb_unit_system';
export const UNIT_CHANGE_EVENT = 'unitsystemchange';

export function getUnitSystem() {
  return localStorage.getItem(KEY) === 'imperial' ? 'imperial' : 'metric';
}
export function isImperial() { return getUnitSystem() === 'imperial'; }

export function setUnitSystem(system) {
  const v = system === 'imperial' ? 'imperial' : 'metric';
  const changed = v !== getUnitSystem();
  localStorage.setItem(KEY, v);
  if (changed) window.dispatchEvent(new CustomEvent(UNIT_CHANGE_EVENT, { detail: { system: v } }));
  return v;
}

export function toggleUnitSystem() {
  return setUnitSystem(isImperial() ? 'metric' : 'imperial');
}

// metric unit → imperial mapping. dec = decimals to show in imperial.
const CONV = {
  '°C':   { to: '°F',   fn: c => c * 9 / 5 + 32, dec: 1 },
  'km/h': { to: 'mph',  fn: k => k * 0.621371,   dec: 1 },
  'm':    { to: 'ft',   fn: m => m * 3.28084,    dec: 1 },
  'km':   { to: 'mi',   fn: k => k * 0.621371,   dec: 2 },
  'kg':   { to: 'lb',   fn: k => k * 2.20462,    dec: 1 },
  'mm':   { to: 'in',   fn: m => m / 25.4,       dec: 2 },
  'cm':   { to: 'in',   fn: c => c / 2.54,       dec: 2 },
  'hPa':  { to: 'inHg', fn: h => h * 0.0295300,  dec: 2 },
};

function round(n, dec) { const f = 10 ** (dec ?? 0); return Math.round(n * f) / f; }

// Convert a numeric value given its metric unit + the metric decimals.
// Returns { value, unit } already rounded for display.
export function convertValue(value, metricUnit, metricDec = 1) {
  const num = Number(value);
  if (Number.isNaN(num)) return { value, unit: metricUnit };
  if (!isImperial() || !CONV[metricUnit]) return { value: round(num, metricDec), unit: metricUnit };
  const c = CONV[metricUnit];
  return { value: round(c.fn(num), c.dec), unit: c.to };
}

// Convert a number to the current system WITHOUT display rounding (for chart
// data, where rounding should happen only at render/tooltip time).
export function convertNumber(value, metricUnit) {
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  if (!isImperial() || !CONV[metricUnit]) return num;
  return CONV[metricUnit].fn(num);
}

// "12.3 °C" / "54.1 °F" honoring the current preference.
export function formatMeasure(value, metricUnit, metricDec = 1) {
  const { value: v, unit } = convertValue(value, metricUnit, metricDec);
  return `${v} ${unit}`;
}

// The imperial label for a metric unit (for static axis/column headers where the
// value itself is converted elsewhere). Falls back to the metric unit.
export function displayUnit(metricUnit) {
  return isImperial() && CONV[metricUnit] ? CONV[metricUnit].to : metricUnit;
}

window.UnitPrefs = { getUnitSystem, isImperial, setUnitSystem, toggleUnitSystem, convertValue, formatMeasure, displayUnit };
